import { useState, useRef, useCallback, type ReactNode } from "react";

export interface ParentLockProps {
  /** Content to render when unlocked */
  children: ReactNode;
  /** How long to hold for unlock (ms) */
  holdDuration?: number;
  /** Optional callback when unlock starts */
  onUnlockStart?: () => void;
  /** Optional callback when unlock is cancelled */
  onUnlockCancel?: () => void;
  /** Optional callback when unlock completes */
  onUnlockComplete?: () => void;
}

/**
 * ParentLock - A simple gesture-based lock for parent-only controls.
 *
 * Requires a long-press (hold) to reveal the children. This provides a simple
 * barrier that young children won't typically figure out, while being effortless
 * for parents.
 *
 * @example
 * <ParentLock holdDuration={800}>
 *   <button onClick={() => setShowEditModal(true)}>
 *     Edit Profile
 *   </button>
 * </ParentLock>
 */
export function ParentLock({
  children,
  holdDuration = 600,
  onUnlockStart,
  onUnlockCancel,
  onUnlockComplete,
}: ParentLockProps) {
  const [isHolding, setIsHolding] = useState(false);
  const [progress, setProgress] = useState(0);
  const [isUnlocked, setIsUnlocked] = useState(false);
  const holdTimer = useRef<ReturnType<typeof setTimeout> | null>(null);
  const progressInterval = useRef<ReturnType<typeof setInterval> | null>(null);
  const startTime = useRef<number>(0);

  const clearTimers = useCallback(() => {
    if (holdTimer.current) {
      clearTimeout(holdTimer.current);
      holdTimer.current = null;
    }
    if (progressInterval.current) {
      clearInterval(progressInterval.current);
      progressInterval.current = null;
    }
  }, []);

  const handleHoldStart = useCallback(() => {
    if (isUnlocked) return;

    setIsHolding(true);
    setProgress(0);
    startTime.current = Date.now();
    onUnlockStart?.();

    // Update progress smoothly
    progressInterval.current = setInterval(() => {
      const elapsed = Date.now() - startTime.current;
      const newProgress = Math.min((elapsed / holdDuration) * 100, 100);
      setProgress(newProgress);
    }, 16);

    // Complete after hold duration
    holdTimer.current = setTimeout(() => {
      clearTimers();
      setIsHolding(false);
      setProgress(100);
      setIsUnlocked(true);
      onUnlockComplete?.();

      // Auto-lock after a delay if no interaction
      setTimeout(() => {
        setIsUnlocked(false);
        setProgress(0);
      }, 5000);
    }, holdDuration);
  }, [holdDuration, isUnlocked, onUnlockStart, onUnlockComplete, clearTimers]);

  const handleHoldEnd = useCallback(() => {
    if (isUnlocked) return;

    clearTimers();
    setIsHolding(false);
    setProgress(0);
    onUnlockCancel?.();
  }, [isUnlocked, clearTimers, onUnlockCancel]);

  // If unlocked, just render the children
  if (isUnlocked) {
    return <>{children}</>;
  }

  return (
    <div
      className="relative cursor-pointer select-none touch-none"
      onMouseDown={handleHoldStart}
      onMouseUp={handleHoldEnd}
      onMouseLeave={handleHoldEnd}
      onTouchStart={handleHoldStart}
      onTouchEnd={handleHoldEnd}
      onTouchCancel={handleHoldEnd}
      role="button"
      tabIndex={0}
      aria-label="Hold to unlock parent controls"
      onKeyDown={(e) => {
        if (e.key === " " || e.key === "Enter") {
          e.preventDefault();
          handleHoldStart();
        }
      }}
      onKeyUp={(e) => {
        if (e.key === " " || e.key === "Enter") {
          handleHoldEnd();
        }
      }}
    >
      {/* Lock indicator */}
      <div
        className={`
          absolute inset-0 z-10
          flex items-center justify-center
          rounded-full
          transition-all duration-200
          ${isHolding ? "bg-magic-100/80" : "bg-transparent"}
        `}
      >
        {isHolding && (
          <div className="relative w-8 h-8">
            {/* Progress ring */}
            <svg
              className="w-8 h-8 transform -rotate-90"
              viewBox="0 0 32 32"
            >
              <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                className="text-neutral-200"
              />
              <circle
                cx="16"
                cy="16"
                r="14"
                fill="none"
                stroke="currentColor"
                strokeWidth="3"
                strokeLinecap="round"
                strokeDasharray={88}
                strokeDashoffset={88 - (88 * progress) / 100}
                className="text-magic-500 transition-all duration-75"
              />
            </svg>
            {/* Lock icon in center */}
            <LockIcon className="absolute inset-0 m-auto w-3.5 h-3.5 text-magic-600" />
          </div>
        )}
      </div>

      {/* Render children but locked */}
      <div className={isHolding ? "opacity-50" : "opacity-100"}>
        {children}
      </div>
    </div>
  );
}

function LockIcon({ className }: { className?: string }) {
  return (
    <svg
      className={className}
      viewBox="0 0 24 24"
      fill="none"
      stroke="currentColor"
      strokeWidth="2.5"
      strokeLinecap="round"
      strokeLinejoin="round"
      aria-hidden="true"
    >
      <rect x="3" y="11" width="18" height="11" rx="2" ry="2" />
      <path d="M7 11V7a5 5 0 0 1 10 0v4" />
    </svg>
  );
}

export default ParentLock;
